import { formatNumber } from '@if/core/utils'
import ErrorIcon from '@mui/icons-material/Error'
import { Alert, Card, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import type { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { memo, useState } from 'react'
import type { Control, FieldErrors, FieldValues } from 'react-hook-form'
import { useController, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddButton from '../Common/Button/AddButton'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import ActionTableCell from '../Common/Table/ActionTableCell'
import InterestRateConfigurationDialog from './InterestRateConfigurationDialog'
import { InterestConfigurationSchema, type InterestConfigurationSchemaType } from './loan-form-schema'

interface InterestConfigurationRowProps {
  interestConfiguration: InterestConfigurationSchemaType
  errors?: FieldErrors<InterestConfigurationSchemaType>
  onDelete: () => void
  onUpdate: (data: InterestConfigurationSchemaType) => void
}

const InterestConfigurationRow = memo(
  ({ interestConfiguration, errors, onUpdate, onDelete }: InterestConfigurationRowProps) => {
    const [open, setOpen] = useState(false)

    return (
      <TableRow>
        <TableCell>
          {interestConfiguration.startDate.toString()}
          {errors?.startDate?.message && (
            <Typography variant="inherit" color="error">
              {errors?.startDate?.message}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {interestConfiguration.endDate.toString()}
          {errors?.endDate?.message && (
            <Typography variant="inherit" color="error">
              {errors?.endDate?.message}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {interestConfiguration.rate && formatNumber(interestConfiguration.rate)} %
          {errors?.rate?.message && (
            <Typography variant="inherit" color="error">
              {errors?.rate?.message}
            </Typography>
          )}
        </TableCell>
        <ActionTableCell>
          <EditIconButton onClick={() => setOpen(true)} />
          <DeleteIconButton onClick={onDelete} />
        </ActionTableCell>
        <InterestRateConfigurationDialog
          open={open}
          defaultValues={interestConfiguration}
          onClose={() => setOpen(false)}
          onSubmit={onUpdate}
        />
      </TableRow>
    )
  },
)

InterestConfigurationRow.displayName = 'InterestConfigurationRow'

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, InterestConfigurationSchemaType>
  values: InterestConfigurationSchemaType[]
}

const InterestRateConfigurationList = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  values,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const { remove, update, append } = useFieldArray({ name, control })
  const {
    fieldState: { error },
  } = useController({ name: name as never, control })
  const [open, setOpen] = useState(false)

  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ padding: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('loan.interestRateConfiguration.configuration')}</Typography>
          <AddButton onClick={() => setOpen(true)} />
        </Stack>
        {error?.message && (
          <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
            {error?.message}
          </Alert>
        )}
        <Table>
          <TableBody>
            {values.map((interestRateConfiguration, index) => {
              return (
                <InterestConfigurationRow
                  key={index}
                  errors={error ? (error as never)[index] : undefined}
                  interestConfiguration={interestRateConfiguration}
                  onDelete={() => remove(index)}
                  onUpdate={(d) => update(index, d as never)}
                />
              )
            })}
          </TableBody>
        </Table>
        <InterestRateConfigurationDialog
          open={open}
          onClose={() => setOpen(false)}
          onSubmit={(d) => append(d as never)}
          defaultValues={InterestConfigurationSchema.getDefault() as unknown as InterestConfigurationSchemaType}
        />
      </Card>
    </Grid>
  )
}

export default InterestRateConfigurationList
